import axios from "axios";
import * as Yup from "yup";
import "yup-phone";

function equalTo(ref, msg) {
  return Yup.mixed().test({
    name: "equalTo",
    exclusive: false,
    message: msg || "${path} must be the same as ${reference}",
    params: {
      reference: ref.path,
    },
    test: function(value) {
      return value === this.resolve(ref);
    },
  });
}

Yup.addMethod(Yup.string, "equalTo", equalTo);

const ValidationHelper = {
  phone: {
    initiate: async (phone) => {
      await axios.post("/api/phone-verifications", { phone });
    },
    validate: async (phone, code) => {
      await axios.get("/api/phone-verifications", {
        params: { phone, code },
      });
    },
  },

  schema: (requiredFields, allFields = [], customRules = {}) => {
    const rules = ValidationHelper.requiredRules(requiredFields);

    for (let i = 0; i < allFields.length; i++) {
      if (allFields[i] === "birth_date") {
        rules[allFields[i]] = Yup.string()
          .matches(
            /^([0-2][0-9]|(3)[0-1])\/(((0)[0-9])|((1)[0-2]))(\/\d{4})?$/,
            "Please enter birth date in the format dd/mm or dd/mm/yyyy"
          )
          .test("birth-date", "Please enter a valid birth date.", function(
            value
          ) {
            if (!value) return true; // Allow empty value
            const parts = value.split("/");
            if (parts.length === 2) return true; // Allow dd/mm format
            if (parts.length === 3) {
              const year = parseInt(parts[2]);
              return year <= 2020; // Validate year if present
            }
            return false; // Invalid format
          });
      }
    }

    return Yup.object({ ...rules, ...customRules });
  },

  requiredRules: (fields) => {
    const rules = {};

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];

      switch (field) {
        case "store_name":
          rules[field] = Yup.string().required("This field is required");
          break;
        case "store_link":
          rules[field] = Yup.string()
            .required("This field is required")
            .matches(
              /.*\.myshopify\.com$/i,
              "Please enter a valid Shopify store URL"
            )
            .matches(/^https.*/i, "HTTPS is required")
            .url("Invalid url");
          break;
        case "expert_categories":
        case "interest_categories":
          rules[field] = Yup.array()
            .required("Please make a selection.")
            .min(1, "Please make a selection.");
          break;
        case "images":
          rules[field] = Yup.array()
            .required("Please upload at least one image.")
            .min(1, "Please upload at least one image.");
          break;
        case "phone":
          rules[field] = Yup.string()
            .phone(null, false, "Phone number is invalid")
            .required("The phone number is required");
          break;
        case "accept_terms":
          rules[field] = Yup.bool()
            .required("You must accept the terms to proceed.")
            .oneOf([true], "You must accept the terms to proceed.");
          break;
        case "sources":
          rules[field] = Yup.array()
            .min(1, "This field is required.")
            .required("This field is required.");
          break;
        case "ebay_title":
          rules[field] = Yup.string()
            .max(79, "eBay Title must be 80 characters or fewer")
            .required("This field is required.");
          break;
        default:
          if (field.substr(field.length - 3) === "_id") {
            rules[field] = Yup.number().required("This field is required.");
            break;
          }

          rules[field] = Yup.string().required("This field is required.");
          break;
      }

      switch (field) {
        case "address":
          rules[field] = Yup.object().shape(ValidationHelper.requiredRules([]));
          break;
        case "case_diameter":
          rules[field] = rules[field].matches(
            // Accept any reading the backend normaliser can parse - one or more
            // measurements (comma or dot decimal, unit optional), or a case shape.
            // The old rule was /^\d+(\.\d{2})? mm$/, which REJECTED the
            // canonical uppercase-MM form it is meant to produce, while happily accepting
            // "40 mm" - one of the duplicate spellings that re-split the Case Size
            // dropdown. App\Support\CaseDiameter on the backend is the source of
            // truth; it rewrites whatever passes here into "41 MM" / "41.5 MM" on save.
            /^\s*[LWH]?\s*\d+([.,]\d+)?\s*(mm|m)?([\s,x\u00d7]+[LWH]?\s*\d+([.,]\d+)?\s*(mm|m)?)*\s*$|^(tonneau|tonnear|round|rectangle|square|oval|cushion)$/i,
            "The case size is invalid."
          );
          break;
        case "company":
          rules[field] = rules[field].min(
            3,
            "The company must be at least 3 characters long."
          );
          break;
        case "email":
          rules[field] = rules[field].matches(
            /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            "Please provide a valid email address."
          );
          break;
        case "price":
          rules[field] = rules[field].nullable();
          break;
        case "password": {
          const errorMessage =
            "The password must contain at least 1 uppercase letter, " +
            "1 lowercase letter, 1 digit, and one special character.";

          rules[field] = rules[field]
            .min(8, "The password must be at least 8 characters long.")
            .oneOf([Yup.ref("password"), null])
            .matches(/[a-z]/, errorMessage)
            .oneOf([Yup.ref("password"), null])
            .matches(/[A-Z]/, errorMessage)
            .oneOf([Yup.ref("password"), null])
            .matches(/[\d]/, errorMessage)
            .oneOf([Yup.ref("password"), null])
            .matches(/[^a-zA-Z\d\s]/, errorMessage);

          break;
        }
        case "password2":
          rules[field] = rules[field].equalTo(
            Yup.ref("password"),
            "The passwords must match."
          );
          break;
        case "year":
          rules[field] = rules[field].matches(
            /^\d{4}$/,
            "The year is invalid."
          );
          break;
      }
    }

    return rules;
  },

  registration: {
    validateContactData: async (values, invitedUser) => {
      const data = {};

      if (!invitedUser) {
        data["email"] = values.email;
        data["phone"] = values.phone;
      }

      await axios.post("/api/users/validate-registration", data);
    },
  },
};

export default ValidationHelper;
